import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

// ─── Company constants ────────────────────────────────────────────────────────

const SELLER = {
  name: 'SIA "Vīnoga.lv"',
  regNo: "40103213927",
  vatNo: "LV40103213927",
  address: ["Baznīcas iela 39A-1,", "Rīga,", "LV-1010"],
  banks: [
    { name: 'AS "Swedbank"', bic: "HABALV2X", account: "LV47HABA0551024333367" },
    { name: "A/S SEB banka", bic: "UNLALV2X", account: "LV05UNLA0050023531312" },
  ] as const,
};

const VAT_RATE = 0.21;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(amount: number | string): string {
  return Number(amount).toFixed(2);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

const ONES = [
  "", "viens", "divi", "trīs", "četri", "pieci", "seši", "septiņi", "astoņi", "deviņi",
  "desmit", "vienpadsmit", "divpadsmit", "trīspadsmit", "četrpadsmit", "piecpadsmit",
  "sešpadsmit", "septiņpadsmit", "astoņpadsmit", "deviņpadsmit",
];
const TENS = ["", "", "divdesmit", "trīsdesmit", "četrdesmit", "piecdesmit",
  "sešdesmit", "septiņdesmit", "astoņdesmit", "deviņdesmit"];
const HUNDREDS = ["", "viens simts", "divi simti", "trīs simti", "četri simti", "pieci simti",
  "seši simti", "septiņi simti", "astoņi simti", "deviņi simti"];

function numToWordsLV(n: number): string {
  if (n === 0) return "nulle";
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h) parts.push(HUNDREDS[h]!);
  if (r < 20) {
    if (r) parts.push(ONES[r]!);
  } else {
    parts.push(TENS[Math.floor(r / 10)]!);
    if (r % 10) parts.push(ONES[r % 10]!);
  }
  return parts.join("-");
}

function amountInWords(total: number): string {
  const euros = Math.floor(total);
  const cents = Math.round((total - euros) * 100);
  const euroWord = numToWordsLV(euros);
  const centWord = String(cents).padStart(2, "0");
  // Capitalise first letter
  const capitalised = euroWord.charAt(0).toUpperCase() + euroWord.slice(1);
  return `${capitalised} eiro un ${centWord} centi`;
}

function fulfilmentLabel(type: string): string {
  return type === "courier" ? "Kurjers" : "Rīgā un Rīgas apkaimē";
}

// ─── Main export ──────────────────────────────────────────────────────────────

export type InvoiceOrder = {
  order_number: string;
  created_at: string;
  customer_email: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  subtotal: string;
  shipping_cost: string;
  coupon_discount: string;
  total_amount: string;
  fulfilment_type: string;
  shipping_address: Record<string, unknown> | null;
  payment_method: string | null;
};

export type InvoiceItem = {
  sku: string;
  name: string;
  qty: number;
  unit_price: string;
  line_total: string;
};

// jsPDF's built-in helvetica is WinAnsi — no Latvian diacritics.
// NotoSans is embedded so ā/ē/ī/ļ/ķ/š/č/ž render correctly.
async function fetchAsBase64(url: string): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`fetch ${url} failed: ${resp.status}`);
  const buf = await resp.arrayBuffer();
  let bin = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

async function registerFonts(doc: jsPDF): Promise<void> {
  const [regularB64, boldB64] = await Promise.all([
    fetchAsBase64("/fonts/NotoSans-Regular.ttf"),
    fetchAsBase64("/fonts/NotoSans-Bold.ttf"),
  ]);
  doc.addFileToVFS("NotoSans-Regular.ttf", regularB64);
  doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
  doc.addFileToVFS("NotoSans-Bold.ttf", boldB64);
  doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
}

export async function generateInvoicePdf(order: InvoiceOrder, items: InvoiceItem[]): Promise<void> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  await registerFonts(doc);
  doc.setFont("NotoSans", "normal");

  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentW = pageW - margin * 2;

  // ── Logo ──────────────────────────────────────────────────────────────────
  try {
    const resp = await fetch("/vinoga-logo.png");
    if (resp.ok) {
      const blob = await resp.blob();
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      // Logo: top-left, height ~18mm, preserve aspect
      doc.addImage(dataUrl, "PNG", margin, 10, 45, 18);
    }
  } catch {
    // Logo load failed — continue without it
  }

  // ── Invoice title block (top right) ───────────────────────────────────────
  doc.setFont("NotoSans", "bold");
  doc.setFontSize(14);
  doc.text("Avansa rēķins", pageW - margin, 14, { align: "right" });

  doc.setFontSize(12);
  doc.text(`#${order.order_number}`, pageW - margin, 21, { align: "right" });

  doc.setFont("NotoSans", "normal");
  doc.setFontSize(10);
  doc.text(`Datums: ${fmtDate(order.created_at)}`, pageW - margin, 27, { align: "right" });

  // ── Horizontal rule ───────────────────────────────────────────────────────
  let y = 34;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── Seller + bank info ────────────────────────────────────────────────────
  const labelGray = [100, 100, 100] as const;
  const colRight = margin + contentW / 2 + 5;

  function labelVal(label: string, val: string, cx: number, cy: number): number {
    doc.setFont("NotoSans", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...labelGray);
    doc.text(label, cx, cy);
    doc.setFont("NotoSans", "normal");
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text(val, cx + 38, cy);
    return cy + 5;
  }

  const sellerStartY = y;
  y = labelVal("Pārdevējs:", SELLER.name, margin, y);
  y = labelVal("Reģistrācijas Nr.:", SELLER.regNo, margin, y);
  y = labelVal("PVN Nr.:", SELLER.vatNo, margin, y);

  // Address (multi-line)
  doc.setFont("NotoSans", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...labelGray);
  doc.text("Juridiskā adrese:", margin, y);
  doc.setFont("NotoSans", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  SELLER.address.forEach((line, i) => {
    doc.text(line, margin + 38, y + i * 4.5);
  });
  y += SELLER.address.length * 4.5 + 1;

  // Banks side by side
  doc.setFont("NotoSans", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...labelGray);
  doc.text("Bankas nosaukums:", margin, y);
  doc.setFont("NotoSans", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(SELLER.banks[0].name, margin + 38, y);
  doc.text(SELLER.banks[1].name, margin + 38 + 45, y);
  y += 5;

  doc.setFont("NotoSans", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...labelGray);
  doc.text("BIC/SWIFT kods:", margin, y);
  doc.setFont("NotoSans", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(SELLER.banks[0].bic, margin + 38, y);
  doc.text(SELLER.banks[1].bic, margin + 38 + 45, y);
  y += 5;

  doc.setFont("NotoSans", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...labelGray);
  doc.text("Konta Nr.:", margin, y);
  doc.setFont("NotoSans", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(SELLER.banks[0].account, margin + 38, y);
  doc.text(SELLER.banks[1].account, margin + 38 + 45, y);
  y += 7;

  // ── Horizontal rule ───────────────────────────────────────────────────────
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── Buyer / order info ────────────────────────────────────────────────────
  const customerName = [order.customer_first_name, order.customer_last_name]
    .filter(Boolean)
    .join(" ");
  const buyerLine = [customerName, order.customer_email, order.customer_phone]
    .filter(Boolean)
    .join(", ");

  y = labelVal("Maksātājs:", buyerLine, margin, y);
  y = labelVal("Pasūtījuma numurs:", `#${order.order_number}`, margin, y);
  y = labelVal("Piegādes veids:", fulfilmentLabel(order.fulfilment_type), margin, y);

  // Shipping address
  const addr = order.shipping_address as Record<string, string> | null;
  if (addr) {
    const addrLine = [addr.street, addr.city, addr.postal_code].filter(Boolean).join(", ");
    if (addrLine) y = labelVal("Piegādes adrese:", addrLine, margin, y);
  }

  const paymentLabel = order.payment_method
    ? order.payment_method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "—";
  y = labelVal("Samaksas veids:", paymentLabel, margin, y);
  y = labelVal("Valūta:", "EUR", margin, y);
  y += 4;

  // ── Line items table ───────────────────────────────────────────────────────
  const tableBody = items.map((item, i) => [
    String(i + 1),
    item.sku,
    item.name,
    String(item.qty),
    fmt(item.unit_price),
    fmt(item.line_total),
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["#", "Kods", "Nosaukums", "Daudzums", "Cena ar PVN, €", "Summa €"]],
    body: tableBody,
    theme: "plain",
    styles: { font: "NotoSans", fontStyle: "normal", fontSize: 9, cellPadding: 2.5 },
    headStyles: {
      font: "NotoSans",
      fontStyle: "bold",
      fontSize: 8,
      textColor: [0, 0, 0],
      fillColor: false,
      lineWidth: { bottom: 0.3 },
      lineColor: [180, 180, 180],
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 16 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 20, halign: "center" },
      4: { cellWidth: 28, halign: "right" },
      5: { cellWidth: 22, halign: "right" },
    },
    didDrawPage: () => {},
  });

  // ── Totals ────────────────────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 6;

  const subtotal = Number(order.subtotal);
  const discount = Number(order.coupon_discount);
  const shipping = Number(order.shipping_cost);
  const total = Number(order.total_amount);
  const vat = total * VAT_RATE / (1 + VAT_RATE);

  const totalsX = pageW - margin - 80;
  const totalsValX = pageW - margin;

  function totalsRow(label: string, val: string, bold = false): void {
    doc.setFont("NotoSans", bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(label, totalsX, y);
    doc.text(val, totalsValX, y, { align: "right" });
    y += 5;
  }

  totalsRow("Kopā (bez atlaides)", fmt(subtotal + discount));
  if (discount > 0) {
    totalsRow("Veikala atlaide:", `-€${fmt(discount)}`);
    totalsRow("Atlaide (kopā):", `-€${fmt(discount)}`);
  }
  totalsRow(`Piegāde - ${fulfilmentLabel(order.fulfilment_type)}`, fmt(shipping));
  totalsRow("Tai skaitā PVN(21%)", fmt(vat));

  y -= 2;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(totalsX, y, pageW - margin, y);
  y += 5;

  totalsRow("Summa apmaksai", fmt(total), true);

  // ── Amount in words ───────────────────────────────────────────────────────
  y += 3;
  doc.setFont("NotoSans", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(`Summa vārdiem: ${amountInWords(total)}`, margin, y);

  // ── Save ──────────────────────────────────────────────────────────────────
  doc.save(`rekini-${order.order_number}.pdf`);
}
